import React, { useState, useEffect } from 'react';
import {
  Popover,
  Typography,
  Box,
  List,
  ListItem,
  Button,
  IconButton,
  Chip,
  Avatar
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import { styled } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import useWebSocket from 'hooks/useWebSocket';
import { formatDistanceToNow } from 'date-fns';


const NotificationHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(10px)',
  '-webkit-backdrop-filter': 'blur(10px)',
  borderRadius: '12px 12px 0 0'
}));

const NotificationItem = styled(ListItem)(({ theme, read }) => ({
  backgroundColor: read ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(8px)',
  '-webkit-backdrop-filter': 'blur(8px)',
  padding: '12px 20px',
  cursor: 'pointer',
  borderBottom: `1px solid rgba(255, 255, 255, 0.2)`,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(12px)',
    '-webkit-backdrop-filter': 'blur(12px)',
  },
  '&:last-child': {
    borderBottom: 'none'
  },
  position: 'relative'
}));

const EmptyNotification = styled(Box)(({ theme }) => ({
  padding: '48px 20px',
  textAlign: 'center',
  color: theme.palette.text.secondary,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  backdropFilter: 'blur(8px)',
  '-webkit-backdrop-filter': 'blur(8px)',
}));

const NotificationFooter = styled(Box)(({ theme }) => ({
  padding: '12px 20px',
  borderTop: `1px solid rgba(255, 255, 255, 0.2)`,
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  backdropFilter: 'blur(8px)',
  '-webkit-backdrop-filter': 'blur(8px)',
  display: 'flex',
  justifyContent: 'center',
  borderRadius: '0 0 12px 12px'
}));

const NotificationLevel = styled(Chip)(({ theme, level }) => {
  const colors = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    default: theme.palette.primary.main
  };

  return {
    backgroundColor: colors[level] || colors.default,
    color: '#fff',
    fontSize: '0.7rem',
    height: 18,
    minWidth: 50,
    '& .MuiChip-label': {
      padding: '0 6px'
    }
  };
});

const UnreadIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 8,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main
}));

const NotificationContent = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  width: '100%'
});

const NotificationAvatar = styled(Avatar)(({ theme, level }) => {
  const colors = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    default: theme.palette.primary.main
  };

  return {
    backgroundColor: colors[level] || colors.default,
    width: 32,
    height: 32,
    fontSize: '1rem'
  };
});


const NotificationsListView = ({ anchorEl, open, onClose, notifications, onNotificationUpdate }) => {
  const { markAsRead } = useWebSocket();
  const navigate = useNavigate();

  // Handle click on a notification
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      // Mark as read in WebSocket
      markAsRead(notification.id);

      // Update local state - only mark the clicked notification as read
      const updatedNotifications = notifications.map(n =>
        n.id === notification.id ? { ...n, read: true } : n
      );

      // Notify parent component about the update
      if (onNotificationUpdate) {
        onNotificationUpdate(updatedNotifications);
      }
    }
    if (notification?.subject === "Pipeline_Status_Update") {
      navigate(`/configure/pipelines?pipeline_run_id=${notification.trace_id }`);
      onClose();
    }

  };

  // Handle clear all notifications
  const handleClearAll = () => {
    // Mark all notifications as read before clearing
    if (notifications.length > 0) {
      notifications.forEach(notification => {
        if (!notification.read) {
          markAsRead(notification.id);
        }
      });
    }

    // Clear notifications by passing empty array to parent
    if (onNotificationUpdate) {
      onNotificationUpdate([]);
    }
    // Close the popup after clearing
    onClose();
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (error) {
      return '';
    }
  };

  // Get level label
  const getLevelLabel = (level) => {
    return level ? level.charAt(0).toUpperCase() + level.slice(1) : 'Info';
  };

  // Get icon for notification level
  const getLevelIcon = (level) => {
    switch (level) {
      case 'success':
        return <CheckCircleIcon fontSize="small" />;
      case 'error':
        return <ErrorIcon fontSize="small" />;
      case 'warning':
        return <WarningIcon fontSize="small" />;
      case 'info':
        return <InfoIcon fontSize="small" />;
      default:
        return <NotificationsIcon fontSize="small" />;
    }
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          width: 420,
          maxHeight: 480,
          overflow: 'hidden',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          '-webkit-backdrop-filter': 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }
      }}
    >
      <NotificationHeader>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Notifications
        </Typography>
        <Box display="flex" gap={1}>
          <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </NotificationHeader>

      {notifications.length > 0 ? (
        <>
          <List sx={{ maxHeight: 360, overflow: 'auto', padding: 0 }}>
            {notifications.map((notification, index) => (
              <NotificationItem
                key={notification.id || index}
                read={notification.read}
                onClick={() => handleNotificationClick(notification)}
              >
                {!notification.read && <UnreadIndicator />}

                <NotificationContent>
                  <NotificationAvatar level={notification.level}>
                    {getLevelIcon(notification.level)}
                  </NotificationAvatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: notification.read ? 400 : 600,
                          color: 'text.primary',
                          lineHeight: 1.4
                        }}
                      >
                        {notification.title}
                      </Typography>
                      <NotificationLevel
                        label={getLevelLabel(notification.level)}
                        level={notification.level}
                        size="small"
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1,
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {notification.message}
                    </Typography>

                    <Box display="flex" justifyContent="flex-end" alignItems="center">
                      {notification.timestamp && (
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(notification.timestamp)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </NotificationContent>
              </NotificationItem>
            ))}
          </List>

          <NotificationFooter>
            <Button
              size="small"
              onClick={handleClearAll}
              disabled={notifications.length === 0}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              Clear all notifications
            </Button>
          </NotificationFooter>
        </>
      ) : (
        <EmptyNotification>
          <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            No notifications yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When you get notifications, they'll show up here
          </Typography>
        </EmptyNotification>
      )}
    </Popover>
  );
};

export default NotificationsListView;
